import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Calendar, Plus, X, Save, Loader2, AlertTriangle, Clock, Trash2 } from 'lucide-react';

const ContractDeadlines = ({ contractId }) => {
  const { api } = useAuth();
  const [expiryDate, setExpiryDate] = useState('');
  const [deadlines, setDeadlines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newDeadline, setNewDeadline] = useState({ label: '', date: '' });

  useEffect(() => {
    fetchDeadlines();
  }, [contractId]);

  const fetchDeadlines = async () => {
    try {
      const response = await api.get(`/contracts/${contractId}/deadlines`);
      setExpiryDate(response.data.expiry_date ? response.data.expiry_date.split('T')[0] : '');
      setDeadlines(response.data.deadlines || []);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveDeadlines = async (updatedExpiry, updatedDeadlines) => {
    setSaving(true);
    try {
      await api.put(`/contracts/${contractId}/deadlines`, {
        expiry_date: updatedExpiry ? new Date(updatedExpiry).toISOString() : null,
        deadlines: updatedDeadlines.map(d => ({
          label: d.label,
          date: new Date(d.date).toISOString()
        }))
      });
      toast.success('Fechas guardadas correctamente');
    } catch (error) {
      toast.error('Error al guardar fechas');
    } finally {
      setSaving(false);
    }
  };

  const addDeadline = () => {
    if (!newDeadline.label.trim() || !newDeadline.date) {
      toast.error('Completa el nombre y fecha');
      return;
    }
    const updated = [...deadlines, { ...newDeadline }];
    setDeadlines(updated);
    setNewDeadline({ label: '', date: '' });
    saveDeadlines(expiryDate, updated);
  };

  const removeDeadline = (idx) => {
    const updated = deadlines.filter((_, i) => i !== idx);
    setDeadlines(updated);
    saveDeadlines(expiryDate, updated);
  };

  const getDaysRemaining = (dateStr) => {
    const now = new Date();
    const date = new Date(dateStr);
    return Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  };

  const getDaysBadge = (dateStr) => {
    const days = getDaysRemaining(dateStr);
    if (days < 0) return <span className="text-xs text-red-400">Vencido</span>;
    if (days === 0) return <span className="text-xs text-red-400">Hoy</span>;
    if (days <= 7) return <span className="text-xs text-yellow-400">{days}d</span>;
    if (days <= 30) return <span className="text-xs text-orange-400">{days}d</span>;
    return <span className="text-xs text-slate-400">{days}d</span>;
  };

  if (loading) {
    return <div className="animate-pulse h-40 bg-slate-800/50 rounded-xl" />;
  }

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <h3 className="text-sm font-semibold flex items-center gap-2 mb-5">
        <Calendar className="w-4 h-4 text-blue-400" />
        Fechas y Vencimientos
      </h3>

      {/* Expiry Date */}
      <div className="mb-5">
        <label className="text-xs text-slate-400 mb-1.5 block">Fecha de Vencimiento del Contrato</label>
        <div className="flex gap-2">
          <input
            type="date"
            value={expiryDate}
            onChange={e => setExpiryDate(e.target.value)}
            className="flex-1 bg-slate-800 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500/50 text-white"
          />
          <button
            onClick={() => saveDeadlines(expiryDate, deadlines)}
            disabled={saving}
            className="px-3 py-2 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm hover:bg-blue-500/30 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </button>
        </div>
        {expiryDate && (
          <p className="text-xs mt-1 flex items-center gap-1">
            {getDaysRemaining(expiryDate) < 0 ? (
              <><AlertTriangle className="w-3 h-3 text-red-400" /> <span className="text-red-400">Contrato vencido</span></>
            ) : getDaysRemaining(expiryDate) <= 30 ? (
              <><Clock className="w-3 h-3 text-yellow-400" /> <span className="text-yellow-400">{getDaysRemaining(expiryDate)} días restantes</span></>
            ) : (
              <><Calendar className="w-3 h-3 text-slate-400" /> <span className="text-slate-400">{getDaysRemaining(expiryDate)} días restantes</span></>
            )}
          </p>
        )}
      </div>

      {/* Custom Deadlines */}
      <div className="mb-4">
        <label className="text-xs text-slate-400 mb-2 block">Fechas Importantes Adicionales</label>
        <div className="space-y-2 mb-3">
          {deadlines.map((dl, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2.5 bg-slate-800/50 rounded-lg">
              <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-sm flex-1 truncate">{dl.label}</span>
              <span className="text-xs text-slate-400">{dl.date?.split('T')[0] || dl.date}</span>
              {getDaysBadge(dl.date)}
              <button
                onClick={() => removeDeadline(idx)}
                className="text-slate-500 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {deadlines.length === 0 && (
            <p className="text-xs text-slate-500 text-center py-2">Sin fechas adicionales</p>
          )}
        </div>

        {/* Add New Deadline */}
        <div className="flex gap-2">
          <input
            type="text"
            value={newDeadline.label}
            onChange={e => setNewDeadline({ ...newDeadline, label: e.target.value })}
            placeholder="Ej: Entrega de documentos"
            className="flex-1 bg-slate-800 border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500/50 text-white placeholder-slate-500"
          />
          <input
            type="date"
            value={newDeadline.date}
            onChange={e => setNewDeadline({ ...newDeadline, date: e.target.value })}
            className="w-32 bg-slate-800 border border-border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-500/50 text-white"
          />
          <button
            onClick={addDeadline}
            className="p-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractDeadlines;
